import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { API_CONFIG } from '../config/api';
import DOMPurify from 'dompurify';

const Popup = () => {
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAndShowPopup();
  }, []);

  const checkAndShowPopup = async () => {
    try {
      const currentPath = window.location.pathname;
      
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getActivePopup&path=${encodeURIComponent(currentPath)}`);
      
      if (!response.ok) {
        
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      if (data && shouldShowPopup(data)) {
        setPopup(data);
        setIsVisible(true);
      }
    } catch (error) {
      console.error('Error fetching popup:', error);
    } finally {
      setLoading(false);
    }
  };

  const shouldShowPopup = (popupData) => {
    const popupKey = `popup_${popupData.id}`;
    const now = new Date();
    
    
    
    // Check if popup was already shown based on frequency
    switch (popupData.display_frequency) {
      case 'once':
        const onceShown = localStorage.getItem(popupKey);
        
        return !onceShown;
      case 'daily':
        const lastShown = localStorage.getItem(`${popupKey}_daily`);
        if (lastShown) {
          const lastShownDate = new Date(lastShown);
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const lastShownDay = new Date(lastShownDate.getFullYear(), lastShownDate.getMonth(), lastShownDate.getDate());
          const shouldShow = today.getTime() > lastShownDay.getTime();
          
          return shouldShow;
        }
        
        return true;
      case 'weekly':
        const lastShownWeekly = localStorage.getItem(`${popupKey}_weekly`);
        if (lastShownWeekly) {
          const lastShownDate = new Date(lastShownWeekly);
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const shouldShow = lastShownDate < weekAgo;
          
          return shouldShow;
        }
        
        return true;
      case 'always':
        
        return true;
      default:
        
        return true;
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    recordPopupShown();
  };

  const handleButtonClick = () => {
    if (popup.button_url) {
      window.open(popup.button_url, '_blank');
    }
    handleClose();
  };

  const recordPopupShown = () => {
    if (!popup) return;
    
    const popupKey = `popup_${popup.id}`;
    const now = new Date();
    
    switch (popup.display_frequency) {
      case 'once':
        localStorage.setItem(popupKey, 'shown');
        break;
      case 'daily':
        localStorage.setItem(`${popupKey}_daily`, now.toISOString());
        break;
      case 'weekly':
        localStorage.setItem(`${popupKey}_weekly`, now.toISOString());
        break;
      case 'always':
        // No need to store for always
        break;
    }
  };

  const getPopupSize = () => {
    switch (popup.size) {
      case 'small': return 'max-w-md w-full';
      case 'large': return 'max-w-4xl w-full';
      default: return 'max-w-2xl w-full';
    }
  };

  const getPopupPosition = () => '';

  if (loading || !isVisible || !popup) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div 
        className={`relative ${getPopupSize()} rounded-lg shadow-xl overflow-hidden`}
        style={{
          backgroundColor: popup.background_color,
          color: popup.text_color
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/80 hover:bg-white transition-all shadow-lg"
          style={{ color: '#374151' }}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content Container */}
        <div className="relative">
          {/* Image - Göster eğer varsa */}
          {popup.image_url && (
            <div className="w-full flex justify-center items-center overflow-hidden">
              <img
                src={
                  popup.image_url.startsWith('http') 
                    ? popup.image_url 
                    : popup.image_url.includes('screenshot_') 
                      ? `${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getScreenshot&file=${popup.image_url.split('/').pop()}`
                      : `${API_CONFIG.BASE_URL}${popup.image_url}`
                }
                alt={popup.title}
                className="object-contain"
                style={{
                  width: popup.image_width || 'auto',
                  maxWidth: popup.image_width ? undefined : '100%',
                  maxHeight: popup.image_height ? undefined : '80vh'
                }}
              />
            </div>
          )}
          
          {/* Text Content - Sadece title veya content varsa göster */}
          {(popup.title || popup.content) && (
            <div className="p-8">
              {popup.title && (
                <h2 className="text-3xl font-bold mb-4">{popup.title}</h2>
              )}
              {popup.content && (
                <div 
                  className="text-lg mb-6 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(popup.content || '') }}
                />
              )}
            </div>
          )}

          {/* Button */}
          {popup.button_text && (
            <div className={`flex justify-center ${(popup.title || popup.content) ? 'px-8 pb-8' : 'p-8'}`}>
              <button
                onClick={handleButtonClick}
                className="px-8 py-4 text-lg rounded-lg font-semibold transition-all hover:opacity-90 hover:scale-105"
                style={{
                  backgroundColor: popup.button_color,
                  color: popup.button_text_color
                }}
              >
                {popup.button_text}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Popup;
